import dayjs from 'dayjs';

import { DateRange } from '../../context/DateProvider';

const getWeekToDate = (): DateRange => {
  return {
    from: dayjs().startOf('week').startOf('day').toDate(),
    to: dayjs().endOf('day').toDate(),
  };
};
export const getWeekToYesterday = (): DateRange => {
  return {
    from: dayjs().startOf('week').startOf('day').toDate(),
    to: dayjs().subtract(1, 'day').endOf('day').toDate(),
  };
};
const getThisMonth = (): DateRange => {
  return {
    from: dayjs().startOf('month').startOf('day').toDate(),
    to: dayjs().endOf('month').toDate(),
  };
};
const getThisWeek = (): DateRange => {
  return {
    from: dayjs().startOf('week').startOf('day').toDate(),
    to: dayjs().endOf('week').toDate(),
  };
};
const getThisYear = (): DateRange => {
  return {
    from: dayjs().startOf('year').startOf('day').toDate(),
    to: dayjs().endOf('year').toDate(),
  };
};
const getMonthToDate = (): DateRange => {
  return {
    from: dayjs().startOf('month').startOf('day').toDate(),
    to: dayjs().endOf('day').toDate(),
  };
};
export const getMonthToYesterday = (): DateRange => {
  return {
    from: dayjs().startOf('month').startOf('day').toDate(),
    to: dayjs().subtract(1, 'day').endOf('day').toDate(),
  };
};
const getYearToDate = (): DateRange => {
  return {
    from: dayjs().startOf('year').startOf('day').toDate(),
    to: dayjs().endOf('day').toDate(),
  };
};
export const getYearToYesterday = (): DateRange => {
  return {
    from: dayjs().startOf('year').startOf('day').toDate(),
    to: dayjs().subtract(1, 'day').endOf('day').toDate(),
  };
};
const getLastYearRange = (): DateRange => {
  return {
    from: dayjs().subtract(1, 'year').startOf('year').toDate(),
    to: dayjs().subtract(1, 'year').endOf('year').toDate(),
  };
};
const getLastThreeMonthsRange = (): DateRange => {
  return {
    from: dayjs().subtract(90, 'day').startOf('day').toDate(),
    to: dayjs().subtract(1, 'day').endOf('day').toDate(),
  };
};
const getLast30Days = (): DateRange => {
  return {
    from: dayjs().subtract(30, 'day').startOf('day').toDate(),
    to: dayjs().subtract(1, 'day').endOf('day').toDate(),
  };
};
const getLastMonthRange = (): DateRange => {
  return {
    from: dayjs().subtract(1, 'month').startOf('month').toDate(),
    to: dayjs().subtract(1, 'month').endOf('month').endOf('day').toDate(),
  };
};
const getLastWeekRange = (): DateRange => {
  return {
    from: dayjs().subtract(7, 'day').startOf('day').toDate(),
    to: dayjs().subtract(1, 'day').endOf('day').toDate(),
  };
};
const getNextWeekRange = (): DateRange => {
  return {
    from: dayjs().add(1, 'day').startOf('day').toDate(),
    to: dayjs().add(7, 'day').endOf('day').toDate(),
  };
};
const getNextMonthRange = (): DateRange => {
  return {
    from: dayjs().add(1, 'month').startOf('month').toDate(),
    to: dayjs().add(1, 'month').endOf('month').toDate(),
  };
};
const getNext8WeeksRange = (): DateRange => {
  return {
    from: dayjs().add(1, 'day').startOf('day').toDate(),
    to: dayjs().add(8, 'week').endOf('day').toDate(),
  };
};
const getNext3MonthsRange = (): DateRange => {
  return {
    from: dayjs().startOf('month').toDate(),
    to: dayjs().add(3, 'month').endOf('day').toDate(),
  };
};
const getNextYearRange = (): DateRange => {
  return {
    from: dayjs().add(1, 'year').startOf('year').toDate(),
    to: dayjs().add(1, 'year').endOf('year').toDate(),
  };
};

// Quarter helpers (no plugin needed — pure month arithmetic)
const getQuarterStartMonth = (date: dayjs.Dayjs): number => Math.floor(date.month() / 3) * 3;

const getLastQuarter = (): DateRange => {
  const now = dayjs();
  const quarterStart = now.month(getQuarterStartMonth(now)).startOf('month');
  return {
    from: quarterStart.subtract(3, 'month').startOf('day').toDate(),
    to: quarterStart.subtract(1, 'day').endOf('day').toDate(),
  };
};
const getQuarterToDate = (): DateRange => {
  const now = dayjs();
  return {
    from: now.month(getQuarterStartMonth(now)).startOf('month').startOf('day').toDate(),
    to: now.endOf('day').toDate(),
  };
};
const getThisQuarter = (): DateRange => {
  const now = dayjs();
  const startMonth = getQuarterStartMonth(now);
  return {
    from: now.month(startMonth).startOf('month').startOf('day').toDate(),
    to: now.month(startMonth + 2).endOf('month').toDate(),
  };
};
const getNextQuarter = (): DateRange => {
  const now = dayjs();
  const nextQuarterStart = now.month(getQuarterStartMonth(now)).startOf('month').add(3, 'month');
  return {
    from: nextQuarterStart.startOf('day').toDate(),
    to: nextQuarterStart.add(2, 'month').endOf('month').toDate(),
  };
};

interface DateRangeOption {
  name: string;
  isPast: boolean;
  group?: string;
  getDateRange: () => DateRange;
}
export type DateRangeOptions = Record<string, DateRangeOption>;

export const DATE_RANGE_GROUP_LABELS: Record<string, string> = {
  recent: 'Recent',
  previous: 'Previous',
  current: 'Current',
  upcoming: 'Upcoming',
  years: 'Years',
  quarters: 'Financial quarters',
};

const YEAR_PRESETS_START = 2020;

const getYearPresets = (): DateRangeOptions => {
  const currentYear = dayjs().year();
  const presets: DateRangeOptions = {};

  for (let y = currentYear; y >= YEAR_PRESETS_START; y--) {
    const shortYear = y.toString().slice(-2);
    presets[`year_${y}`] = {
      name: `Year ${shortYear}`,
      isPast: y < currentYear,
      group: 'years',
      getDateRange: () => ({
        from: dayjs(new Date(y, 0, 1)).startOf('day').toDate(),
        to: dayjs(new Date(y, 11, 31)).endOf('day').toDate(),
      }),
    };
  }

  return presets;
};

export const DATE_RANGES: DateRangeOptions = {
  // Recent (rolling windows + to-date)
  previousWeek: { name: 'Last 7 days', isPast: true, group: 'recent', getDateRange: getLastWeekRange },
  previous30Days: { name: 'Last 30 days', isPast: true, group: 'recent', getDateRange: getLast30Days },
  previous90Days: { name: 'Last 90 days', isPast: true, group: 'recent', getDateRange: getLastThreeMonthsRange },
  weekToDate: { name: 'Week to date', isPast: true, group: 'recent', getDateRange: getWeekToDate },
  monthToDate: { name: 'Month to date', isPast: true, group: 'recent', getDateRange: getMonthToDate },
  quarterToDate: { name: 'Quarter to date', isPast: true, group: 'recent', getDateRange: getQuarterToDate },
  yearToDate: { name: 'Year to date', isPast: true, group: 'recent', getDateRange: getYearToDate },
  // Previous (calendar-aligned past periods)
  previousMonth: { name: 'Previous month', isPast: true, group: 'previous', getDateRange: getLastMonthRange },
  previousQuarter: { name: 'Previous quarter', isPast: true, group: 'previous', getDateRange: getLastQuarter },
  previousYear: { name: 'Previous year', isPast: true, group: 'previous', getDateRange: getLastYearRange },
  // Current (full calendar periods)
  thisWeek: { name: 'This week', isPast: false, group: 'current', getDateRange: getThisWeek },
  thisMonth: { name: 'This month', isPast: false, group: 'current', getDateRange: getThisMonth },
  thisQuarter: { name: 'This quarter', isPast: false, group: 'current', getDateRange: getThisQuarter },
  thisYear: { name: 'This year', isPast: false, group: 'current', getDateRange: getThisYear },
  // Upcoming
  nextWeek: { name: 'Next week', isPast: false, group: 'upcoming', getDateRange: getNextWeekRange },
  nextMonth: { name: 'Next month', isPast: false, group: 'upcoming', getDateRange: getNextMonthRange },
  nextQuarter: { name: 'Next quarter', isPast: false, group: 'upcoming', getDateRange: getNextQuarter },
  next8Weeks: { name: 'Next 8 weeks', isPast: false, group: 'upcoming', getDateRange: getNext8WeeksRange },
  next3Months: { name: 'Next 3 months', isPast: false, group: 'upcoming', getDateRange: getNext3MonthsRange },
  nextYear: { name: 'Next year', isPast: false, group: 'upcoming', getDateRange: getNextYearRange },
  // Years (individual calendar years, most recent first)
  ...getYearPresets(),
};

export function convertLocalDateToUTCIgnoringTimezone(date: Date) {
  const timestamp = Date.UTC(
    date?.getFullYear(),
    date?.getMonth(),
    date?.getDate(),
    date?.getHours(),
    date?.getMinutes(),
    date?.getSeconds(),
    date?.getMilliseconds(),
  );

  return new Date(timestamp);
}
